import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import { cn } from "@faire/react/tools/cn";
import { CreditCard } from "@faire/slate-icons/CreditCard";
import { Synchronized } from "@faire/slate-icons/Synchronized";
import { Button } from "@faire/slate/components/button";
import type { FocusEvent, KeyboardEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { FairePaySquare, InsiderKey40 } from "./ProgramIcons";

type GrowthItemId = "insider" | "fairePay" | "terms" | "pos";

interface GrowthItem {
  id: GrowthItemId;
  label: string;
  program: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaUrl: string;
  icon: ReactNode;
}

const GROWTH_ITEMS: GrowthItem[] = [
  {
    id: "insider",
    label: "Free shipping & discounts",
    program: "Insider",
    title: "Free shipping on thousands of unique brands",
    description:
      "Try new products with less risk and keep more savings to put back into your store.",
    ctaLabel: "Learn about Insider",
    ctaUrl: "https://www.faire.com/insider",
    icon: <InsiderKey40 height="20" width="20" />,
  },
  {
    id: "fairePay",
    label: "3% cash back",
    program: "Faire Pay",
    title: "Faire Pay is built for wholesale buying",
    description:
      "Get 3% off every Faire purchase paid with Faire Pay, directly increasing your profit.",
    ctaLabel: "Activate to start saving",
    ctaUrl: "https://www.faire.com/faire-pay",
    icon: <FairePaySquare height="20" width="20" />,
  },
  {
    id: "terms",
    label: "Stock now, pay later (up to $30K)",
    program: "Payment terms",
    title: "Order new inventory today, pay 60 days later",
    description:
      "Payment terms help you buy the right amount of inventory while protecting your cash flow.",
    ctaLabel: "View payment terms",
    ctaUrl: "https://www.faire.com/net-terms",
    icon: (
      <CreditCard
        className="text-fs-icon-primary h-(--slate-dimensions-5) w-(--slate-dimensions-5)"
        height="20"
        width="20"
      />
    ),
  },
  {
    id: "pos",
    label: "Sync orders with your POS",
    program: "POS integration",
    title: "Add new products to your point of sale in just 5 minutes",
    description:
      "Connect Shopify, Square, or Clover to move Faire products into your existing workflow.",
    ctaLabel: "Connect your point of sale",
    ctaUrl: "https://www.faire.com/point-of-sale/about",
    icon: (
      <Synchronized
        className="text-fs-icon-primary h-(--slate-dimensions-5) w-(--slate-dimensions-5)"
        height="20"
        width="20"
      />
    ),
  },
];

const DRAWER_ID = "growth-strip-drawer";
const DRAWER_CLOSE_DELAY_MS = 160;

export const BenefitStrip = (): ReactNode => {
  const [activeItemId, setActiveItemId] = useState<GrowthItemId | null>(null);
  const stripRef = useRef<HTMLElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const stripLabel = usePlaygroundLocalMsg("Faire growth tools");
  const activeItem = GROWTH_ITEMS.find(({ id }) => id === activeItemId);

  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openItem = (itemId: GrowthItemId) => {
    cancelClose();
    setActiveItemId(itemId);
  };

  const closeDrawer = () => {
    cancelClose();
    setActiveItemId(null);
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      setActiveItemId(null);
      closeTimerRef.current = null;
    }, DRAWER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (activeItemId === null) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!stripRef.current?.contains(event.target as Node)) {
        if (closeTimerRef.current !== null) {
          window.clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        setActiveItemId(null);
      }
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        if (closeTimerRef.current !== null) {
          window.clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        setActiveItemId(null);
        document.getElementById(`growth-strip-${activeItemId}`)?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [activeItemId]);

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      scheduleClose();
    }
  };

  const handleTriggerKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    currentItemId: GrowthItemId
  ) => {
    const currentIndex = GROWTH_ITEMS.findIndex(
      ({ id }) => id === currentItemId
    );
    const lastIndex = GROWTH_ITEMS.length - 1;
    let nextIndex: number | null = null;

    if (event.key === "ArrowRight") {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    } else if (event.key === "ArrowLeft") {
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = lastIndex;
    }

    if (nextIndex === null) {
      return;
    }

    event.preventDefault();
    const nextItem = GROWTH_ITEMS[nextIndex];
    openItem(nextItem.id);
    document.getElementById(`growth-strip-${nextItem.id}`)?.focus();
  };

  return (
    <aside
      ref={stripRef}
      aria-label={stripLabel}
      className="bg-fs-surface-secondary border-fs-border-muted relative hidden h-(--slate-dimensions-10) w-full border-b lg:block"
      onBlur={handleBlur}
      onFocus={cancelClose}
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
    >
      <div className="mx-auto flex h-full w-full max-w-4xl items-center justify-center gap-(--slate-spacing-group-default)">
        {GROWTH_ITEMS.map((item) => (
          <LocalizedGrowthTrigger
            key={item.id}
            item={item}
            isActive={activeItemId === item.id}
            onActivate={() => openItem(item.id)}
            onClick={() => openItem(item.id)}
            onKeyDown={(event) => handleTriggerKeyDown(event, item.id)}
          />
        ))}
      </div>

      {activeItem ? (
        <div
          id={DRAWER_ID}
          aria-live="polite"
          aria-label={stripLabel}
          className="bg-fs-surface-primary border-fs-border-muted shadow-elevation-medium absolute inset-x-0 top-full z-50 border-b"
          role="region"
        >
          <LocalizedGrowthDrawer item={activeItem} onNavigate={closeDrawer} />
        </div>
      ) : null}
    </aside>
  );
};

const LocalizedGrowthTrigger = ({
  item,
  isActive,
  onActivate,
  onClick,
  onKeyDown,
}: {
  item: GrowthItem;
  isActive: boolean;
  onActivate: () => void;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}): ReactNode => {
  const label = usePlaygroundLocalMsg(item.label);

  return (
    <button
      id={`growth-strip-${item.id}`}
      aria-controls={isActive ? DRAWER_ID : undefined}
      aria-expanded={isActive}
      className={cn(
        "focusable hover:bg-fs-surface-primary active:bg-fs-surface-tertiary",
        "flex h-full cursor-pointer items-center justify-center",
        "gap-(--slate-spacing-element-xsmall) px-(--slate-spacing-element-small)",
        "border-b-2 border-transparent transition-colors motion-reduce:transition-none",
        {
          "bg-fs-surface-primary border-fs-action-border-default": isActive,
        }
      )}
      type="button"
      onClick={onClick}
      onFocus={onActivate}
      onKeyDown={onKeyDown}
      onMouseEnter={onActivate}
    >
      <span className="flex h-(--slate-dimensions-5) w-(--slate-dimensions-5) shrink-0 items-center justify-center">
        {item.icon}
      </span>
      <span className="type-fs-label text-fs-text-primary text-[13px] tracking-normal whitespace-nowrap">
        {label}
      </span>
    </button>
  );
};

const LocalizedGrowthDrawer = ({
  item,
  onNavigate,
}: {
  item: GrowthItem;
  onNavigate: () => void;
}): ReactNode => {
  const program = usePlaygroundLocalMsg(item.program);
  const title = usePlaygroundLocalMsg(item.title);
  const description = usePlaygroundLocalMsg(item.description);
  const ctaLabel = usePlaygroundLocalMsg(item.ctaLabel);

  return (
    <div className="mx-auto grid min-h-[calc(var(--slate-dimensions-18)*2)] w-full max-w-4xl grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-center gap-(--slate-spacing-section-default) px-(--slate-spacing-element-large) py-(--slate-spacing-group-default)">
      <div className="flex min-w-0 items-center gap-(--slate-spacing-group-default)">
        <div className="bg-fs-surface-secondary rounded-fs-container-default flex h-(--slate-dimensions-12) w-(--slate-dimensions-12) shrink-0 items-center justify-center">
          {item.icon}
        </div>
        <div className="flex min-w-0 flex-col gap-(--slate-spacing-element-xsmall)">
          <p className="type-fs-label-medium text-fs-text-subdued">{program}</p>
          <h2 className="type-fs-subheading-medium">{title}</h2>
        </div>
      </div>

      <div className="border-fs-border-muted flex min-w-0 flex-col items-start gap-(--slate-spacing-element-large) border-l pl-(--slate-spacing-section-default)">
        <p className="type-fs-paragraph text-fs-text-subdued">{description}</p>
        <Button
          linkTo={item.ctaUrl}
          size="small"
          target="_blank"
          onClick={onNavigate}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};
